import { useState, useEffect } from 'react'
import { useYear } from '../auth/YearContext'
import { subscribeYear, setSkattekontoCloud, type CloudYearDoc } from '../lib/cloudStore'
import { WinButtons, currentSize, type CardSizeProps } from './WinButtons'

const fmt = (n: number) =>
  n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

interface Props extends CardSizeProps { companyId: string; companyName: string }

export function SkattekontoCard(props: Props) {
  const { companyId, companyName } = props
  const size = currentSize(props)
  const { year } = useYear()
  const [data, setData] = useState<CloudYearDoc>({ year, months: [] })
  const [editing, setEditing] = useState(false)
  const [input, setInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    setErr(null)
    try {
      return subscribeYear(companyId, year, setData)
    } catch (e) {
      setErr((e as Error).message)
      return () => {}
    }
  }, [companyId, year])

  const saldo = data.skattekontoSaldo ?? 0
  const updated = data.skattekontoUpdated
    ? new Date(data.skattekontoUpdated).toLocaleDateString('sv-SE')
    : null
  const color = saldo >= 0 ? 'value--pos' : 'value--neg'

  const save = async () => {
    const n = Number(input.replace(/\s/g, '').replace(',', '.'))
    if (isNaN(n)) return
    setSaving(true)
    setErr(null)
    try {
      await setSkattekontoCloud(companyId, year, n)
      setEditing(false)
      setInput('')
    } catch (e) {
      setErr('Kunde inte spara: ' + (e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  if (size === 'min') {
    return (
      <div className="card card--collapsed" style={{ borderLeftColor: '#f59e0b' }}>
        <div className="card__header-row">
          <span className="card__label" style={{ margin: 0 }}>Skattekonto</span>
          <span className={'card__mini-value ' + (data.skattekontoSaldo != null ? color : '')}>
            {data.skattekontoSaldo != null ? `${fmt(saldo)} kr` : '\u2014'}
          </span>
          <WinButtons current="min" {...props} />
        </div>
      </div>
    )
  }

  return (
    <div className={'card card--accent' + (size === 'med' ? ' card--medium' : '')} style={{ borderLeftColor: '#f59e0b' }}>
      <div className="card__header-row">
        <div className="card__label" style={{ margin: 0 }}>Skattekonto {'\u2014'} {companyName}</div>
        <WinButtons current={size} {...props} />
      </div>
      <div className={'card__value ' + (data.skattekontoSaldo != null ? color : '')}>
        {data.skattekontoSaldo != null ? `${fmt(saldo)} kr` : '—'}
      </div>
      <div className="card__sub">
        {updated ? `Senast uppdaterad ${updated}` : 'Inget saldo inlagt'}
      </div>
      <div className="card__divider" />
      {!editing ? (
        <button
          className="cs__button"
          style={{ fontSize: '0.85rem' }}
          onClick={() => {
            setInput(saldo ? String(saldo) : '')
            setEditing(true)
          }}
        >
          {data.skattekontoSaldo != null ? '✏️ Uppdatera saldo' : '+ Lägg in saldo'}
        </button>
      ) : (
        <div className="inline-edit">
          <input
            type="text"
            inputMode="decimal"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="123456"
            autoFocus
            disabled={saving}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save()
              if (e.key === 'Escape') setEditing(false)
            }}
          />
          <button onClick={save} disabled={saving}>
            {saving ? 'Sparar…' : 'Spara'}
          </button>
        </div>
      )}
      {err && (
        <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: 6 }}>
          {err}
        </div>
      )}
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.75rem' }}>
        Tips: uppdatera 5:e varje månad (kalenderpåminnelse redan satt).
      </div>
    </div>
  )
}
